import React, { useState, useEffect, useMemo } from 'react';
import { apiClient } from '../../utils/apiClient';
import { useTheme } from '../../contexts/ThemeContext';
import { useNotification } from '../../contexts/NotificationContext';
import DocumentationViewer from '../DocumentationViewer';
import DocumentationEditor from '../DocumentationEditor';
import IconUpload from '../IconUpload';
import Portal from '../Portal';
import FilterSidebar from '../FilterSidebar';
import ConfirmationModal from '../ConfirmationModal';

const WorkflowManagement = () => {
  const { isDarkMode } = useTheme();
  const { showNotification } = useNotification();
  const [workflows, setWorkflows] = useState([]);
  const [filteredWorkflows, setFilteredWorkflows] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [servers, setServers] = useState([]);
  const [filteredServers, setFilteredServers] = useState([]);
  const [serverSearchTerm, setServerSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingWorkflow, setEditingWorkflow] = useState(null);
  const [selectedWorkflows, setSelectedWorkflows] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, workflowId: null, isBulk: false });
  
  // Filter and sort state
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [sortBy, setSortBy] = useState('name');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    endpoint: '',
    status: 'UNAVAILABLE',
    mcpServerIds: [],
    documentation: '',
    category: '',
    tags: '',
    iconAlt: ''
  });
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [showDocumentation, setShowDocumentation] = useState(false);
  const [showDocumentationEditor, setShowDocumentationEditor] = useState(false);
  const [showIconUpload, setShowIconUpload] = useState(false);
  const [isUploadingIcon, setIsUploadingIcon] = useState(false);
  const [isExportingCSV, setIsExportingCSV] = useState(false);
  const [isImportingCSV, setIsImportingCSV] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  // Get unique categories and statuses from workflows
  const categories = useMemo(() => {
    const categoryMap = {};
    workflows.forEach(workflow => {
      if (workflow.category) {
        categoryMap[workflow.category] = (categoryMap[workflow.category] || 0) + 1;
      }
    });
    
    // Add "All" option at the beginning
    const allCategories = [
      { value: 'ALL', label: 'All', count: workflows.length }
    ];
    
    // Add specific categories
    Object.entries(categoryMap).forEach(([category, count]) => {
      allCategories.push({
        value: category,
        label: category,
        count
      });
    });
    
    return allCategories;
  }, [workflows]);

  const statuses = useMemo(() => {
    const statusMap = {};
    workflows.forEach(workflow => {
      statusMap[workflow.status] = (statusMap[workflow.status] || 0) + 1;
    });
    
    // Ensure both AVAILABLE and UNAVAILABLE are always present
    const allStatuses = [
      { value: 'AVAILABLE', label: 'Available', count: statusMap['AVAILABLE'] || 0 },
      { value: 'UNAVAILABLE', label: 'Unavailable', count: statusMap['UNAVAILABLE'] || 0 }
    ];
    
    return allStatuses;
  }, [workflows]);

  // Comprehensive filtering and sorting for workflows
  const processedWorkflows = useMemo(() => {
    let filtered = [...workflows];

    // Search filter - only search by name and description
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(workflow =>
        workflow.name.toLowerCase().includes(term) ||
        (workflow.description && workflow.description.toLowerCase().includes(term))
      );
    }

    // Category filter
    if (selectedCategories.length > 0 && !selectedCategories.includes('ALL')) {
      filtered = filtered.filter(workflow => 
        workflow.category && selectedCategories.includes(workflow.category)
      );
    }

    // Status filter
    if (selectedStatuses.length > 0) {
      filtered = filtered.filter(workflow => 
        selectedStatuses.includes(workflow.status)
      );
    }

    // Sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'status':
          return a.status.localeCompare(b.status);
        case 'category':
          return (a.category || '').localeCompare(b.category || '');
        case 'created':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'updated':
          return new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt);
        default:
          return 0;
      }
    });

    return filtered;
  }, [workflows, searchTerm, selectedCategories, selectedStatuses, sortBy]);

  useEffect(() => {
    setFilteredWorkflows(processedWorkflows);
  }, [processedWorkflows]);

  useEffect(() => {
    if (serverSearchTerm.trim() === '') {
      setFilteredServers(servers);
    } else {
      const filtered = servers.filter(server =>
        server.name.toLowerCase().includes(serverSearchTerm.toLowerCase()) ||
        server.status.toLowerCase().includes(serverSearchTerm.toLowerCase())
      );
      setFilteredServers(filtered);
    }
  }, [servers, serverSearchTerm]);

  useEffect(() => {
    setSelectAll(selectedWorkflows.length > 0 && selectedWorkflows.length === filteredWorkflows.length);
  }, [selectedWorkflows, filteredWorkflows]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [workflowsResponse, serversResponse] = await Promise.all([
        apiClient.get('/workflows'),
        apiClient.get('/mcp-servers')
      ]);
      
      setWorkflows(workflowsResponse.data.data || []);
      setServers(serversResponse.data.data || []);
      setFilteredServers(serversResponse.data.data || []);
    } catch (err) {
      setError('Failed to fetch data');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    if (e.target.name === 'mcpServerIds') {
      const serverId = e.target.value;
      const isChecked = e.target.checked;
      
      setFormData(prevData => ({
        ...prevData,
        mcpServerIds: isChecked 
          ? [...prevData.mcpServerIds, serverId]
          : prevData.mcpServerIds.filter(id => id !== serverId)
      }));
    } else {
      setFormData({
        ...formData,
        [e.target.name]: e.target.value
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        mcpServerIds: formData.mcpServerIds || []
      };

      if (editingWorkflow) {
        await apiClient.put(`/workflows/${editingWorkflow.id}`, submitData);
      } else {
        await apiClient.post('/workflows', submitData);
      }
      
      setShowForm(false);
      setEditingWorkflow(null);
      setFormData({ name: '', description: '', endpoint: '', status: 'UNAVAILABLE', mcpServerIds: [] });
      fetchData();
    } catch (err) {
      setError('Failed to save workflow');
      console.error('Error saving workflow:', err);
    }
  };

  const handleEdit = (workflow) => {
    setEditingWorkflow(workflow);
    setFormData({
      name: workflow.name,
      description: workflow.description || '',
      endpoint: workflow.endpoint || '',
      status: workflow.status || 'UNAVAILABLE',
      mcpServerIds: workflow.mcpServers ? workflow.mcpServers.map(ws => ws.mcpServer.id) : [],
      documentation: workflow.documentation || '',
      category: workflow.category || '',
      tags: workflow.tags || '',
      iconAlt: workflow.iconAlt || ''
    });
    setShowForm(true);
  };

  const handleDelete = (workflowId) => {
    setDeleteConfirm({ isOpen: true, workflowId, isBulk: false });
  };

  const confirmDelete = async () => {
    const { workflowId, isBulk } = deleteConfirm;
    
    try {
      if (isBulk) {
        const deletePromises = selectedWorkflows.map(id => 
          apiClient.delete(`/workflows/${id}`)
        );
        await Promise.all(deletePromises);
        
        setWorkflows(workflows.filter(workflow => !selectedWorkflows.includes(workflow.id)));
        setSelectedWorkflows([]);
        setSelectAll(false);
        showNotification(`Successfully deleted ${selectedWorkflows.length} workflow(s)`, 'success');
      } else {
        await apiClient.delete(`/workflows/${workflowId}`);
        setWorkflows(workflows.filter(workflow => workflow.id !== workflowId));
        showNotification('Workflow deleted successfully', 'success');
      }
    } catch (err) {
      const errorMessage = isBulk 
        ? 'Failed to delete some workflows' 
        : 'Failed to delete workflow';
      setError(errorMessage);
      showNotification(errorMessage, 'error');
      console.error('Error deleting workflow:', err);
    } finally {
      setDeleteConfirm({ isOpen: false, workflowId: null, isBulk: false });
    }
  };

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedWorkflows(filteredWorkflows.map(workflow => workflow.id));
    } else {
      setSelectedWorkflows([]);
    }
  };

  const handleSelectWorkflow = (workflowId, checked) => {
    if (checked) {
      setSelectedWorkflows([...selectedWorkflows, workflowId]);
    } else {
      setSelectedWorkflows(selectedWorkflows.filter(id => id !== workflowId));
    }
  };

  const handleBulkDelete = () => {
    if (selectedWorkflows.length === 0) {
      setError('Please select workflows to delete');
      return;
    }
    setDeleteConfirm({ isOpen: true, workflowId: null, isBulk: true });
  };

  const handleStatusChange = async (workflowId, newStatus) => {
    try {
      await apiClient.patch(`/workflows/${workflowId}/status`, { status: newStatus });
      fetchData();
    } catch (err) {
      setError('Failed to update workflow status');
      console.error('Error updating status:', err);
    }
  };

  const handleViewDocumentation = (workflow) => {
    setSelectedWorkflow(workflow);
    setShowDocumentation(true);
  };

  const handleEditDocumentation = (workflow) => {
    setSelectedWorkflow(workflow);
    setShowDocumentationEditor(true);
  };

  const handleSaveDocumentation = async (docData) => {
    try {
      await apiClient.patch(`/workflows/${selectedWorkflow.id}/documentation`, {
        documentation: docData.documentation,
        category: docData.category,
        tags: docData.tags,
        iconAlt: docData.iconAlt,
        documentedBy: 'Admin' // In a real app, get from auth context
      });
      
      setShowDocumentationEditor(false);
      setSelectedWorkflow(null);
      fetchData();
    } catch (err) {
      setError('Failed to save documentation');
      console.error('Error saving documentation:', err);
    }
  };

  const handleUploadIcon = async (file) => {
    setIsUploadingIcon(true);
    try {
      const formData = new FormData();
      formData.append('icon', file);
      formData.append('iconAlt', selectedWorkflow?.iconAlt || '');

      await apiClient.patch(`/workflows/${selectedWorkflow.id}/icon`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setShowIconUpload(false);
      setSelectedWorkflow(null);
      fetchData();
    } catch (err) {
      setError('Failed to upload icon');
      console.error('Error uploading icon:', err);
    } finally {
      setIsUploadingIcon(false);
    }
  };

  const handleRemoveIcon = async () => {
    try {
      await apiClient.patch(`/workflows/${selectedWorkflow.id}/icon`, {
        iconUrl: null,
        iconAlt: ''
      });
      
      setShowIconUpload(false);
      setSelectedWorkflow(null);
      fetchData();
    } catch (err) {
      setError('Failed to remove icon');
      console.error('Error removing icon:', err);
    }
  };

  const handleIconAltChange = (altText) => {
    if (selectedWorkflow) {
      setSelectedWorkflow({ ...selectedWorkflow, iconAlt: altText });
    }
  };

  // Filter and sort handlers
  const handleCategoryChange = (category) => {
    setSelectedCategories(prev => 
      prev.includes(category) 
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const handleStatusFilterChange = (status) => {
    setSelectedStatuses(prev => 
      prev.includes(status) 
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };

  const handleClearFilters = () => {
    setSelectedCategories([]);
    setSelectedStatuses([]);
    setSearchTerm('');
  };

  const handleSortChange = (newSortBy) => {
    setSortBy(newSortBy);
  };

  const handleExportCSV = async () => {
    try {
      setIsExportingCSV(true);
      const response = await apiClient.get('/workflows/export/csv', {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'workflows.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to export CSV');
      console.error('Error exporting CSV:', err);
    } finally {
      setIsExportingCSV(false);
    }
  };

  const handleImportCSV = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError('Please select a CSV file');
      return;
    }

    try {
      setIsImportingCSV(true);
      const formData = new FormData();
      formData.append('csvFile', file);

      const response = await apiClient.post('/workflows/import/csv', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        const { created, updated, errors } = response.data.data;
        let message = `Import completed: ${created} created, ${updated} updated`;
        const notificationType = errors.length > 0 ? 'warning' : 'success';
        if (errors.length > 0) {
          message += `, ${errors.length} error${errors.length > 1 ? 's' : ''}`;
        }
        showNotification(message, notificationType, 5000);
        fetchData(); // Refresh the list
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to import CSV';
      setError(errorMessage);
      showNotification(errorMessage, 'error', 5000);
      console.error('Error importing CSV:', err);
    } finally {
      setIsImportingCSV(false);
      // Reset the file input
      event.target.value = '';
    }
  };

  const sortOptions = [
    { value: 'name', label: 'Name' },
    { value: 'status', label: 'Status' },
    { value: 'category', label: 'Category' },
    { value: 'created', label: 'Date Created' },
    { value: 'updated', label: 'Last Updated' }
  ];


  if (loading) {
    return <div className="flex-center">Loading workflows...</div>;
  }

  return (
    <div>
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ marginBottom: '1rem' }}>Workflow Management</h2>
        <div className="flex gap-10" style={{ alignItems: 'center' }}>
          <button
            className="btn btn-success"
            onClick={handleExportCSV}
            disabled={isExportingCSV}
            style={{ 
              opacity: isExportingCSV ? 0.6 : 1,
              cursor: isExportingCSV ? 'not-allowed' : 'pointer'
            }}
          >
            {isExportingCSV ? 'Exporting...' : 'Export CSV'}
          </button>
          <label className="btn btn-info" style={{ cursor: 'pointer', margin: 0 }}>
            {isImportingCSV ? 'Importing...' : 'Import CSV'}
            <input
              type="file"
              accept=".csv"
              onChange={handleImportCSV}
              disabled={isImportingCSV}
              style={{ display: 'none' }}
            />
          </label>
          <button
            className="btn btn-primary"
            onClick={() => {
              setShowForm(!showForm);
              setEditingWorkflow(null);
              setFormData({ name: '', description: '', endpoint: '', status: 'UNAVAILABLE', mcpServerIds: [] });
            }}
          >
            {showForm ? 'Cancel' : 'Add Workflow'}
          </button>
        </div>
      </div>


      {error && (
        <div className="alert alert-danger">
          {error}
        </div>
      )}

      {showForm && (
        <div className="card" style={{ marginBottom: '2rem' }}>
          <h3>{editingWorkflow ? 'Edit Workflow' : 'Add New Workflow'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label" htmlFor="name">
                Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                className="form-input"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="description">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                className="form-input"
                value={formData.description}
                onChange={handleChange}
                rows="3"
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="endpoint">
                Endpoint *
              </label>
              <input
                type="url"
                id="endpoint"
                name="endpoint"
                className="form-input"
                value={formData.endpoint}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="status">
                Status *
              </label>
              <select
                id="status"
                name="status"
                className="form-input"
                value={formData.status}
                onChange={handleChange}
              >
                <option value="UNAVAILABLE">Unavailable</option>
                <option value="AVAILABLE">Available</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="category">
                Category
              </label>
              <input
                type="text"
                id="category"
                name="category"
                className="form-input"
                value={formData.category}
                onChange={handleChange}
                placeholder="e.g., Bioinformatics, Data Analysis"
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="tags">
                Tags (comma-separated)
              </label>
              <input
                type="text"
                id="tags"
                name="tags"
                className="form-input"
                value={formData.tags}
                onChange={handleChange}
                placeholder="e.g., genomics, python, analysis"
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="iconAlt">
                Icon Alt Text
              </label>
              <input
                type="text"
                id="iconAlt"
                name="iconAlt"
                className="form-input"
                value={formData.iconAlt}
                onChange={handleChange}
                placeholder="Descriptive text for the icon"
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="documentation">
                Documentation (Markdown)
              </label>
              <textarea
                id="documentation"
                name="documentation"
                className="form-input"
                value={formData.documentation}
                onChange={handleChange}
                rows="6"
                placeholder="Write documentation in Markdown format..."
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Associated MCP Servers
              </label>
              
              {/* Server Search Box */}
              <div style={{ marginBottom: '12px' }}>
                <input
                  type="text"
                  placeholder="Search servers..."
                  value={serverSearchTerm}
                  onChange={(e) => setServerSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: `1px solid ${isDarkMode ? '#555' : '#ddd'}`,
                    borderRadius: '4px',
                    backgroundColor: isDarkMode ? '#2a2a2a' : 'white',
                    color: isDarkMode ? 'white' : '#333',
                    fontSize: '14px',
                    outline: 'none',
                    transition: 'border-color 0.2s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#007bff';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = isDarkMode ? '#555' : '#ddd';
                  }}
                />
              </div>

              <div 
                style={{ 
                  border: `1px solid ${isDarkMode ? '#555' : '#ddd'}`,
                  borderRadius: '6px',
                  padding: '16px',
                  backgroundColor: isDarkMode ? '#2a2a2a' : 'white',
                  maxHeight: '200px',
                  overflowY: 'auto'
                }}
              >
                {filteredServers.length === 0 ? (
                  <div style={{ 
                    textAlign: 'center', 
                    color: isDarkMode ? '#aaa' : '#666',
                    padding: '20px',
                    fontStyle: 'italic'
                  }}>
                    {serverSearchTerm ? 'No servers found matching your search.' : 'No servers available.'}
                  </div>
                ) : (
                  filteredServers.map((server) => (
                  <div key={server.id} style={{ marginBottom: '12px' }}>
                    <label 
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        cursor: 'pointer',
                        color: isDarkMode ? 'white' : '#333',
                        padding: '4px 0',
                        transition: 'background-color 0.2s ease',
                        borderRadius: '4px',
                        paddingLeft: '8px',
                        paddingRight: '8px',
                        margin: '0 -8px'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = isDarkMode ? '#3a3a3a' : '#f8f9fa';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                      }}
                    >
                      <input
                        type="checkbox"
                        name="mcpServerIds"
                        value={server.id}
                        checked={formData.mcpServerIds.includes(server.id)}
                        onChange={handleChange}
                        style={{ 
                          marginRight: '12px',
                          width: '16px',
                          height: '16px',
                          accentColor: '#007bff'
                        }}
                      />
                      <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                        <span style={{ fontWeight: '500', marginRight: '8px' }}>
                          {server.name}
                        </span>
                        <span 
                          style={{ 
                            color: server.status === 'ACTIVE' ? '#28a745' : '#dc3545',
                            fontSize: '0.85em',
                            fontWeight: '500',
                            padding: '2px 6px',
                            borderRadius: '12px',
                            backgroundColor: server.status === 'ACTIVE' ? 'rgba(40, 167, 69, 0.1)' : 'rgba(220, 53, 69, 0.1)',
                            border: `1px solid ${server.status === 'ACTIVE' ? 'rgba(40, 167, 69, 0.3)' : 'rgba(220, 53, 69, 0.3)'}`
                          }}
                        >
                          {server.status}
                        </span>
                      </div>
                    </label>
                  </div>
                  ))
                )}
              </div>
              <small className="form-text" style={{ color: isDarkMode ? '#aaa' : '#666', marginTop: '8px' }}>
                Select one or more MCP servers to associate with this workflow
              </small>
            </div>

            <div className="flex gap-10">
              <button type="submit" className="btn btn-primary">
                {editingWorkflow ? 'Update' : 'Create'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowForm(false);
                  setEditingWorkflow(null);
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Main Content with Sidebar */}
      <div className="mcp-servers-layout">
        {/* Left Side: Filter Sidebar */}
        <div className="filter-sidebar-wrapper">
          <FilterSidebar
            categories={categories}
            statuses={statuses}
            selectedCategories={selectedCategories}
            selectedStatuses={selectedStatuses}
            onCategoryChange={handleCategoryChange}
            onStatusChange={handleStatusFilterChange}
            onClearFilters={handleClearFilters}
            title="Filters"
          />
        </div>

        {/* Right Side: Search, Sort, and Main Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Search and Sort Controls */}
          <div className="search-sort-container" style={{
            backgroundColor: isDarkMode ? '#1a1a1a' : '#f8f9fa',
            border: `1px solid ${isDarkMode ? '#333' : '#e9ecef'}`,
            borderRadius: '8px',
            padding: 'clamp(1rem, 3vw, 1.5rem)',
            marginBottom: '1.5rem'
          }}>
            {/* Search Element */}
            <div className="search-wrapper" style={{ position: 'relative' }}>
              <div style={{
                position: 'absolute',
                left: '0.75rem',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#007bff',
                fontSize: '1rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search by name or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem 0.75rem 2.5rem',
                  border: `1px solid ${isDarkMode ? '#555' : '#ddd'}`,
                  borderRadius: '8px',
                  backgroundColor: isDarkMode ? '#2a2a2a' : 'white',
                  color: isDarkMode ? 'white' : '#333',
                  fontSize: '0.9rem',
                  outline: 'none',
                  transition: 'all 0.3s ease'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#007bff';
                  e.target.style.boxShadow = '0 0 0 2px rgba(0, 123, 255, 0.25)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = isDarkMode ? '#555' : '#ddd';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            {/* Sort Element - Anchored to the right */}
            <div className="sort-wrapper" style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.5rem', 
              marginLeft: 'auto'
            }}>
              <span style={{
                color: isDarkMode ? '#aaa' : '#666',
                fontSize: '0.9rem',
                fontWeight: '500'
              }}>
                Showing {filteredWorkflows.length} of {workflows.length}
              </span>
              
              <div style={{ position: 'relative' }}>
                <button
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: isDarkMode ? '#2a2a2a' : 'white',
                    border: `1px solid ${isDarkMode ? '#555' : '#ddd'}`,
                    borderRadius: '6px',
                    color: isDarkMode ? 'white' : '#333',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = isDarkMode ? '#3a3a3a' : '#f8f9fa';
                    e.target.style.borderColor = '#007bff';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = isDarkMode ? '#2a2a2a' : 'white';
                    e.target.style.borderColor = isDarkMode ? '#555' : '#ddd';
                  }}
                >
                  Sort by {sortOptions.find(opt => opt.value === sortBy)?.label || 'popularity'}
                  <span>▼</span>
                </button>
                
                {/* Dropdown Menu */}
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  backgroundColor: isDarkMode ? '#2a2a2a' : 'white',
                  border: `1px solid ${isDarkMode ? '#555' : '#ddd'}`,
                  borderRadius: '6px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  zIndex: 1000,
                  minWidth: '150px',
                  marginTop: '0.25rem',
                  display: 'none' // This would be controlled by state in a real implementation
                }}>
                  {sortOptions.map((option) => (
                    <div
                      key={option.value}
                      style={{
                        padding: '0.75rem 1rem',
                        cursor: 'pointer',
                        color: isDarkMode ? 'white' : '#333',
                        fontSize: '0.9rem',
                        borderBottom: `1px solid ${isDarkMode ? '#333' : '#f0f0f0'}`,
                        transition: 'background-color 0.2s ease'
                      }}
                      onClick={() => handleSortChange(option.value)}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = isDarkMode ? '#3a3a3a' : '#f8f9fa';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                      }}
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          {filteredWorkflows.length === 0 ? (
            <div className="card">
              <p>{workflows.length === 0 ? 'No workflows found.' : 'No matching workflows found.'}</p>
            </div>
          ) : (
            <div>
              {/* Selection Controls */}
              <div className="card" style={{ marginBottom: '1rem' }}>
                <div className="flex-between" style={{ alignItems: 'center' }}>
                  <div className="flex" style={{ alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      style={{ marginRight: '8px' }}
                    />
                    <label style={{ margin: 0, fontWeight: '500' }}>
                      Select All ({filteredWorkflows.length} workflows)
                    </label>
                  </div>
                  <button
                    className="btn btn-danger"
                    onClick={handleBulkDelete}
                    disabled={selectedWorkflows.length === 0}
                    style={{ 
                      opacity: selectedWorkflows.length === 0 ? 0.5 : 1,
                      cursor: selectedWorkflows.length === 0 ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Delete Selected ({selectedWorkflows.length})
                  </button>
                </div>
              </div>

              <div className="card">
                <div style={{ overflowX: 'auto', width: '100%' }}>
                  <table className="table" style={{ minWidth: '600px' }}>
                    <thead>
                      <tr>
                        <th>Select</th>
                        <th>Name</th>
                        <th>Description</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredWorkflows.map((workflow) => (
                        <tr key={workflow.id}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedWorkflows.includes(workflow.id)}
                              onChange={(e) => handleSelectWorkflow(workflow.id, e.target.checked)}
                              style={{ marginRight: '8px' }}
                            />
                          </td>
                          <td>{workflow.name}</td>
                          <td>{workflow.description || '-'}</td>
                          <td>
                            <select
                              value={workflow.status}
                              onChange={(e) => handleStatusChange(workflow.id, e.target.value)}
                              style={{
                                padding: '2px 6px',
                                fontSize: '12px',
                                border: '1px solid #ddd',
                                borderRadius: '4px'
                              }}
                            >
                              <option value="UNAVAILABLE">Unavailable</option>
                              <option value="AVAILABLE">Available</option>
                            </select>
                          </td>
                          <td>
                            <div className="flex gap-10" style={{ alignItems: 'center', flexWrap: 'nowrap' }}>
                              <button
                                className="btn btn-secondary"
                                style={{ padding: '4px 8px', fontSize: '12px' }}
                                onClick={() => handleEdit(workflow)}
                              >
                                Edit
                              </button>
                              {workflow.documentation && (
                                <button
                                  className="btn btn-info"
                                  style={{ padding: '4px 8px', fontSize: '12px' }}
                                  onClick={() => handleViewDocumentation(workflow)}
                                >
                                  Docs
                                </button>
                              )}
                              <button
                                className="btn btn-danger"
                                style={{ padding: '4px 8px', fontSize: '12px' }}
                                onClick={() => handleDelete(workflow.id)}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Documentation Viewer Modal */}
      {showDocumentation && selectedWorkflow && (
        <Portal>
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '2rem',
            overflow: 'auto'
          }}>
            <div style={{
              backgroundColor: isDarkMode ? '#2a2a2a' : 'white',
              borderRadius: '8px',
              maxWidth: '800px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              position: 'relative',
              margin: 'auto',
              boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
            }}>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowDocumentation(false);
                  setSelectedWorkflow(null);
                }}
                style={{
                  position: 'absolute',
                  top: '1rem',
                  right: '1rem',
                  zIndex: 10000,
                  background: 'rgba(255,255,255,0.9)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  fontSize: '18px',
                  fontWeight: 'bold'
                }}
              >
                ×
              </button>
              <DocumentationViewer
                documentation={selectedWorkflow.documentation}
                title={selectedWorkflow.name}
                iconUrl={selectedWorkflow.iconUrl}
                iconAlt={selectedWorkflow.iconAlt}
                category={selectedWorkflow.category}
                tags={selectedWorkflow.tags}
                lastDocumentedAt={selectedWorkflow.lastDocumentedAt}
                documentedBy={selectedWorkflow.documentedBy}
                onEdit={() => {
                  setShowDocumentation(false);
                  handleEditDocumentation(selectedWorkflow);
                }}
                showEditButton={true}
              />
            </div>
          </div>
        </Portal>
      )}

      {/* Documentation Editor Modal */}
      {showDocumentationEditor && selectedWorkflow && (
        <Portal>
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '2rem',
            overflow: 'auto'
          }}>
            <div style={{
              backgroundColor: isDarkMode ? '#2a2a2a' : 'white',
              borderRadius: '8px',
              maxWidth: '900px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              position: 'relative',
              margin: 'auto',
              boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
            }}>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowDocumentationEditor(false);
                  setSelectedWorkflow(null);
                }}
                style={{
                  position: 'absolute',
                  top: '1rem',
                  right: '1rem',
                  zIndex: 10000,
                  background: 'rgba(255,255,255,0.9)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  fontSize: '18px',
                  fontWeight: 'bold'
                }}
              >
                ×
              </button>
              <DocumentationEditor
                initialDocumentation={selectedWorkflow.documentation || ''}
                initialCategory={selectedWorkflow.category || ''}
                initialTags={selectedWorkflow.tags || ''}
                initialIconAlt={selectedWorkflow.iconAlt || ''}
                onSave={handleSaveDocumentation}
                onCancel={() => {
                  setShowDocumentationEditor(false);
                  setSelectedWorkflow(null);
                }}
                title={`Edit Documentation - ${selectedWorkflow.name}`}
              />
            </div>
          </div>
        </Portal>
      )}

      {/* Icon Upload Modal */}
      {showIconUpload && selectedWorkflow && (
        <Portal>
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '2rem',
            overflow: 'auto'
          }}>
            <div style={{
              backgroundColor: isDarkMode ? '#2a2a2a' : 'white',
              borderRadius: '8px',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              position: 'relative',
              margin: 'auto',
              boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
            }}>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowIconUpload(false);
                  setSelectedWorkflow(null);
                }}
                style={{
                  position: 'absolute',
                  top: '1rem',
                  right: '1rem',
                  zIndex: 10000,
                  background: 'rgba(255,255,255,0.9)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  fontSize: '18px',
                  fontWeight: 'bold'
                }}
              >
                ×
              </button>
              <IconUpload
                currentIconUrl={selectedWorkflow.iconUrl}
                currentIconAlt={selectedWorkflow.iconAlt}
                onUpload={handleUploadIcon}
                onRemove={handleRemoveIcon}
                onAltTextChange={handleIconAltChange}
                isUploading={isUploadingIcon}
              />
            </div>
          </div>
        </Portal>
      )}

      <ConfirmationModal
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, workflowId: null, isBulk: false })}
        onConfirm={confirmDelete}
        title={deleteConfirm.isBulk ? `Delete ${selectedWorkflows.length} Workflow(s)?` : 'Delete Workflow?'}
        message={deleteConfirm.isBulk 
          ? `Are you sure you want to delete ${selectedWorkflows.length} workflow(s)? This action cannot be undone.`
          : 'Are you sure you want to delete this workflow? This action cannot be undone.'}
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonStyle="danger"
      />
    </div>
  );
};

export default WorkflowManagement;
